import { useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { useActiveRosterState, useWorkspaceMembers } from "../../hooks";
import { auth } from "../../lib/firebase";
import { classifyRosterSlug } from "../../lib/rosterIdentity";

// Block characters that would break git branches, filesystem paths, or
// Firestore doc IDs — keep in sync with server/modules/team/restApi.ts,
// server/services/rosterService.ts, and packages/relay-daemon/src/cli.ts.
const INVALID_AGENT_NAME = /[\s/\\~^:?*[.]/;

// ─── Server-side roster helpers ─────────────────────────────────────────────
// Roster writes go through POST /api/relay/roster/{add,remove} which use
// the Admin SDK to bypass Firestore security rules.

async function addAgentToRoster(agentId: string, _workspaceId: string) {
  const resp = await fetch('/api/relay/roster/add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
    body: JSON.stringify({ agentId }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? `HTTP ${resp.status}`);
  }
}

async function removeAgentFromRoster(agentId: string, _workspaceId: string) {
  const resp = await fetch('/api/relay/roster/remove', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
    body: JSON.stringify({ agentId }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? `HTTP ${resp.status}`);
  }
}

type PartnerKind = 'builder' | 'advisor';

async function fetchPartnerSettings(): Promise<{ eligibility: Record<string, boolean>; kinds: Record<string, PartnerKind> }> {
  const resp = await fetch('/api/workspace/partners', { headers: await getAuthHeader() });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = (await resp.json()) as { partners?: { agent_id: string; board_eligible?: boolean; kind?: PartnerKind }[] };
  const eligibility: Record<string, boolean> = {};
  const kinds: Record<string, PartnerKind> = {};
  for (const p of data.partners ?? []) {
    eligibility[p.agent_id] = p.board_eligible !== false;
    kinds[p.agent_id] = p.kind === 'advisor' ? 'advisor' : 'builder';
  }
  return { eligibility, kinds };
}

async function savePartnerKind(agentId: string, kind: PartnerKind) {
  const resp = await fetch(`/api/workspace/partners/${encodeURIComponent(agentId)}/kind`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
    body: JSON.stringify({ kind }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? `HTTP ${resp.status}`);
  }
}

async function saveBoardEligible(agentId: string, eligible: boolean) {
  const resp = await fetch(`/api/workspace/partners/${encodeURIComponent(agentId)}/board-eligible`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
    body: JSON.stringify({ eligible }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? `HTTP ${resp.status}`);
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function FixedAgentCard({ name, role }: { name: string; role: string }) {
  return (
    <div className="sc-item-row">
      <div>
        <div className="sc-item-name">{name}</div>
        <div className="sc-item-meta">{role}</div>
      </div>
    </div>
  );
}

// ─── Dynamic AI Partners ──────────────────────────────────────────────────────

interface DynamicPartnersSectionProps {
  workspaceId: string;
  devAgents: string[];
  /** Re-fetches the roster; called after a successful add or delete. */
  refreshRoster: () => void;
  /** Only a Lead may remove a partner (POST /api/relay/roster/remove is Lead-only). */
  currentUserIsLead: boolean;
}

function DynamicPartnersSection({ workspaceId, devAgents, refreshRoster, currentUserIsLead }: DynamicPartnersSectionProps) {
  const [adding, setAdding] = useState(false);
  const [newAgentId, setNewAgentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState(false);
  // Absent from the map means eligible (matches the server default).
  const [eligibility, setEligibility] = useState<Record<string, boolean>>({});
  // Absent from the map means builder (matches the server default for an available partner).
  const [kinds, setKinds] = useState<Record<string, PartnerKind>>({});
  const [eligibleErrors, setEligibleErrors] = useState<Record<string, string>>({});
  const agentKey = devAgents.join("|");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await fetchPartnerSettings();
        if (!cancelled) {
          setEligibility(settings.eligibility);
          setKinds(settings.kinds);
        }
      } catch {
        // Read failure leaves every switch at the eligible default; a save
        // still reports its own error.
      }
    })();
    return () => { cancelled = true; };
  }, [agentKey]);

  async function handleEligibleChange(agentId: string, next: boolean) {
    const prev = eligibility[agentId] !== false;
    setEligibility((m) => ({ ...m, [agentId]: next }));
    setEligibleErrors((m) => { const { [agentId]: _drop, ...rest } = m; return rest; });
    try {
      await saveBoardEligible(agentId, next);
    } catch (err: unknown) {
      setEligibility((m) => ({ ...m, [agentId]: prev }));
      const msg = err instanceof Error ? err.message : "request failed";
      setEligibleErrors((m) => ({ ...m, [agentId]: `Could not update ${agentId}: ${msg}` }));
    }
  }

  async function handleKindChange(agentId: string, next: PartnerKind) {
    const prev = kinds[agentId] ?? 'builder';
    setKinds((m) => ({ ...m, [agentId]: next }));
    // The server clears availability when a partner becomes an advisor.
    if (next === 'advisor') setEligibility((m) => ({ ...m, [agentId]: false }));
    setEligibleErrors((m) => { const { [agentId]: _drop, ...rest } = m; return rest; });
    try {
      await savePartnerKind(agentId, next);
    } catch (err: unknown) {
      setKinds((m) => ({ ...m, [agentId]: prev }));
      const msg = err instanceof Error ? err.message : "request failed";
      setEligibleErrors((m) => ({ ...m, [agentId]: `Could not update ${agentId}: ${msg}` }));
    }
  }
  const trimmedNewAgentId = newAgentId.trim();
  const invalidFormat = trimmedNewAgentId !== "" && INVALID_AGENT_NAME.test(trimmedNewAgentId);

  async function handleAdd() {
    const id = newAgentId.trim();
    if (!id) return;
    if (classifyRosterSlug(id) !== "ai-partner") {
      setError(`"${id}" is a reserved name and cannot be added.`);
      return;
    }
    if (devAgents.includes(id)) {
      setError(`"${id}" is already in the roster.`);
      return;
    }
    if (INVALID_AGENT_NAME.test(id)) {
      setError("Agent names can't contain spaces or special characters. Use letters, numbers, hyphens, and underscores only.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await addAgentToRoster(id, workspaceId);
      refreshRoster();
      setNewAgentId("");
      setAdding(false);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 8000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to add agent";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(agentId: string) {
    setConfirmingDelete(null);
    setDeleting(agentId);
    try {
      await removeAgentFromRoster(agentId, workspaceId);
      refreshRoster();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to remove agent";
      setEligibleErrors((m) => ({ ...m, [agentId]: msg }));
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      {/* track:cc-ai-partners-dynamic (seq #43375) — no "Developers"
          sub-heading here: this list now renders every roster slug
          classifyRosterSlug calls "ai-partner" (dev-*, but also HRT/
          design/mem/claude per Travis's ruling at seq #43417), so a
          heading that says "Developers" would misdescribe the row a
          moment after shipping. */}
      {!adding && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <button className="btn sm" onClick={() => { setAdding(true); setError(null); }}>
            + Add
          </button>
        </div>
      )}

      {devAgents.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: "8px 0", fontFamily: "var(--font-ui)" }}>
          No other AI partners in roster. Click + Add to register one.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {devAgents.map((id) => (
          <div key={id} className="sc-item-row">
            <span className="sc-item-name" style={{ fontWeight: 500 }}>{id}</span>
            <select
              aria-label={`${id} is a`}
              value={kinds[id] ?? 'builder'}
              onChange={(e) => handleKindChange(id, e.target.value as PartnerKind)}
              style={{ fontSize: 12, fontFamily: "var(--font-ui)", marginLeft: "auto", marginRight: 12 }}
            >
              <option value="builder">Builder</option>
              <option value="advisor">Advisor</option>
            </select>
            {(kinds[id] ?? 'builder') === 'builder' && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontFamily: "var(--font-ui)", marginRight: 12 }}>
                <input
                  type="checkbox"
                  role="switch"
                  checked={eligibility[id] !== false}
                  onChange={(e) => handleEligibleChange(id, e.target.checked)}
                />
                Available for board builds and reviews
                <span style={{ color: "var(--muted)" }}>({eligibility[id] !== false ? "on" : "off"})</span>
              </label>
            )}
            {currentUserIsLead && confirmingDelete !== id && (
              <button
                className="btn sm danger"
                onClick={() => setConfirmingDelete(id)}
                disabled={deleting === id}
                style={{ padding: "2px 8px", fontSize: 11 }}
              >
                {deleting === id ? "..." : "Delete"}
              </button>
            )}
            {currentUserIsLead && confirmingDelete === id && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontFamily: "var(--font-ui)" }}>
                Remove “{id}” from the roster?
                <button className="btn sm danger" onClick={() => handleDelete(id)} style={{ padding: "2px 8px", fontSize: 11 }}>Remove</button>
                <button className="btn sm" onClick={() => setConfirmingDelete(null)} style={{ padding: "2px 8px", fontSize: 11 }}>Cancel</button>
              </span>
            )}
          </div>
        ))}
      </div>

      {Object.entries(eligibleErrors).map(([id, msg]) => (
        <div key={id} role="alert" style={{ fontSize: 12, color: "var(--berry)", fontFamily: "var(--font-ui)", marginTop: 6 }}>{msg}</div>
      ))}

      {justAdded && (
        <div className="sc-callout success" style={{ marginTop: 10 }}>
          ✓ Agent added to roster.{" "}
          <strong>Run relay start</strong> (or restart Relay Helper) for this agent to receive work.
        </div>
      )}

      {adding && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            autoFocus
            type="text"
            placeholder="Agent ID (e.g. dev-5)"
            value={newAgentId}
            onChange={(e) => setNewAgentId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") { setAdding(false); setError(null); } }}
            style={{
              padding: "7px 10px",
              fontSize: 13,
              fontFamily: "var(--font-ui)",
              border: `1.5px solid ${invalidFormat ? "var(--berry)" : "var(--line)"}`,
              borderRadius: 6,
              background: "#fff",
              color: "var(--ink)",
              width: "100%",
              boxSizing: "border-box" as const,
            }}
          />
          {invalidFormat && (
            <div style={{ fontSize: 12, color: "var(--berry)", fontFamily: "var(--font-ui)" }}>
              Agent names can't contain spaces or special characters. Use letters, numbers, hyphens, and underscores only.
            </div>
          )}
          {!invalidFormat && error && <div style={{ fontSize: 12, color: "var(--berry)", fontFamily: "var(--font-ui)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn primary"
              onClick={handleAdd}
              disabled={saving || !trimmedNewAgentId || invalidFormat}
            >
              {saving ? "Adding..." : "Add"}
            </button>
            <button
              className="btn sm"
              onClick={() => { setAdding(false); setNewAgentId(""); setError(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main section ─────────────────────────────────────────────────────────────

interface AITeamSectionProps {
  workspaceId: string;
}

export function AITeamSection({ workspaceId }: AITeamSectionProps) {
  const { roster, error: rosterError, retry: retryRoster } = useActiveRosterState(workspaceId);
  // track:cc-ai-partners-dynamic (seq #43375) — every roster slug
  // classifyRosterSlug calls "ai-partner" renders here (dev-*, and per
  // Travis's explicit ruling at seq #43417, also HRT/design/mem/claude —
  // "the roster is variable by design," no curated exclusion list).
  // "pinned" (relay/advisor) render as the two FixedAgentCards below,
  // unconditionally, regardless of roster contents; "human" and
  // "infrastructure" render nowhere in this section.
  const { members } = useWorkspaceMembers(workspaceId);
  const currentUserIsLead = members.some(
    (m) => m.user_id === auth.currentUser?.uid && m.role === "human_lead"
  );
  const devAgents = roster.filter((id) => classifyRosterSlug(id) === "ai-partner");
  const invalidRosterNames = roster.filter((id) => INVALID_AGENT_NAME.test(id));

  return (
    <section id="team-section-ai" className="tp-group ai">
      <div className="tp-group-head">
        <div className="tp-card-title">AI partners</div>
        <span className="tp-small" style={{ marginLeft: "auto" }}>{devAgents.length} connected</span>
      </div>

      {invalidRosterNames.length > 0 && (
        <div className="sc-callout warn" style={{ marginBottom: 12 }}>
          ⚠️ Some team member names contain characters that may cause issues with agent dispatch: {invalidRosterNames.join(", ")}.
        </div>
      )}

      {rosterError && roster.length === 0 && (
        <div className="sc-callout warn" role="alert" style={{ marginBottom: 12 }}>
          Couldn't load your AI team. Try again.{" "}
          <button type="button" onClick={retryRoster}>Try again</button>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Relay */}
        <FixedAgentCard name="Relay" role="AI Coordinator" />

        {/* Advisor */}
        <FixedAgentCard name="Advisor" role="Planning Partner" />

        {/* Divider */}
        <div style={{ borderTop: "1px solid var(--line-soft)", margin: "4px 0" }} />

        {/* Dynamic AI partners — everything classifyRosterSlug calls "ai-partner" */}
        <DynamicPartnersSection workspaceId={workspaceId} devAgents={devAgents} refreshRoster={retryRoster} currentUserIsLead={currentUserIsLead} />
      </div>
    </section>
  );
}
